import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { useCambiosProveedor, type Proveedor } from './datos';

// «Proveedores - Eliminar proveedor» del Figma. Sigue a `EliminarSucursal.tsx`:
// confirma antes de la baja y dice qué pasa con lo que ya existe. La baja es
// lógica: los gastos que lo usan se conservan.

interface Props {
  /** Solo lo que dice el texto. Sirve la fila del listado y la ficha de la edición. */
  proveedor: Pick<Proveedor, 'id' | 'name' | 'code'> | null;
  onCerrar: () => void;
  /** Después de eliminarlo. La edición vuelve al listado; el listado no hace nada más. */
  onEliminado?: () => void;
}

export function EliminarProveedor({ proveedor, onCerrar, onEliminado }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosProveedor();
  const [eliminando, setEliminando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  function cerrar() {
    if (eliminando) return;
    setProblema(null);
    onCerrar();
  }

  async function eliminar() {
    if (!proveedor) return;
    setEliminando(true);
    setProblema(null);
    try {
      const error = await cambios.eliminar(String(proveedor.id));
      if (error) {
        setProblema(error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta'));
        return;
      }
    } catch {
      // Sin red la petición lanza; sin esto «Cancelar» quedaba apagado para siempre (#124).
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    } finally {
      setEliminando(false);
    }
    avisar({ titulo: t('admin.proveedores.aviso.eliminado', { nombre: proveedor.name }) });
    onCerrar();
    onEliminado?.();
  }

  return (
    <Ventana
      abierta={proveedor !== null}
      titulo={t('admin.proveedores.eliminar.titulo')}
      descripcion={
        proveedor && t('admin.proveedores.eliminar.texto', { nombre: proveedor.name, codigo: proveedor.code })
      }
      acciones={
        <>
          <Boton variante="texto" disabled={eliminando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={eliminando} aria-busy={eliminando} onClick={() => void eliminar()}>
            {t('admin.proveedores.eliminar.boton')}
          </Boton>
        </>
      }
    >
      {problema && <AvisoError aviso={{ titulo: t('admin.proveedores.error.titulo'), texto: problema }} />}
    </Ventana>
  );
}
